import { RiskTag } from '@/components/chariot-ui';
import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import { getRiskTagSeverity } from '@/sections/vulnerabilities/components/VulnerabilityDrawer';
import { VulnerabilityStatusComponent } from '@/sections/vulnerabilities/components/VulnerabilityStatus';
import type { Risk } from '@/types';

/**
 * Builds column definitions for Risk entity tables
 *
 * Returns columns for:
 * - status: Risk status (Pending/Open/Closed/Accepted/Rejected) - FIRST
 * - name: Vulnerability name (bold purple) with DNS below - SECOND
 *
 * Column order follows platform convention (status-first) to align with
 * main tables (Assets, Seeds, Vulnerabilities) for consistent UX.
 * In global mode, Customer Name is prepended, resulting in:
 * [Customer Name, Status, Vulnerability]
 *
 * The name column uses NameColumn component to display:
 * - Risk name in bold purple text
 * - DNS field below in smaller gray text
 *
 * Status is extracted from the 'class' field's first character:
 * - 'T' → Pending
 * - 'O' → Open
 * - 'R' → Closed
 * - 'I' → Accepted
 * - 'D' → Rejected
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildRiskColumns();
 * // Returns array with status and name columns (status first)
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildRiskColumns(): Columns<Risk> {
  return [
    {
      id: 'status',
      label: 'Status',
      ...getColumnWidth('risk', 'status'),
      cell: (item: Risk) =>
        item?.status ? (
          <VulnerabilityStatusComponent vulnerability={item} />
        ) : (
          '-'
        ),
    },
    {
      id: '',
      label: 'Severity',
      ...getColumnWidth('risk', 'priority'),
      sorting: false,
      cell: (item: Risk) => {
        if (!item?.status) return '-';
        const severity = getRiskTagSeverity(item.status);
        return <RiskTag severity={severity} size="xs" />;
      },
    },
    {
      id: 'name',
      label: 'Vulnerability',
      ...getColumnWidth('risk', 'name'),
      cell: (item: Risk) => {
        const title = item.title || item.name;
        if (!title) {
          return '-';
        }
        return <NameColumn name={title} description={item.dns} />;
      },
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
